import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { roleDefinitionFor } from '@/access/roles'
import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { myNav } from '@/components/px/nav'
import { selectActiveOrgId, selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { healthTone, isoDayLabel, phaseDisplayName } from '@/features/projects/format'
import { fetchMyWork, selectMyWorkError, selectMyWorkLoading, selectMyWorkRows } from '@/features/my-work/slices/myWorkSlice'
import { BoardVerdictActions } from '@/features/board/components/BoardVerdictActions'
import { BuildQueue } from '@/features/board/components/BuildQueue'
import { ReviewCard } from '@/features/board/components/ReviewCard'
import { RunLogPanel } from '@/features/board/components/RunLogPanel'
import { selectSelectedTask } from '@/features/board/slices/boardSlice'
import { asApiError } from '@/services/apiError'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * Two boards, one component (PX-W7.4, TASK-41CE1DE2), both wired onto the
 * same `features/board` feature plus the already-live `features/my-work`
 * slice:
 *
 *  - `multi`  — the CONSULTANT's board (`/my/board`): the engagements strip
 *    from `features/my-work`, and the kanban for ONE engagement's real
 *    tasks at a time — the project the `?project=` filter names, defaulting
 *    to the first my-work row — narrowed to the caller (assignee OR
 *    reviewer). A true cross-project card projection does not exist
 *    server-side; that gap is a change request on TASK-FCC2C3E4, not built
 *    here (AC-9, Decision 3).
 *  - `single` — the CLIENT EMPLOYEE's board (`/my/project`): the one
 *    project their my-work row names, no filter control, no fixture person,
 *    project or work package (AC-10). Its shell chrome is built from route
 *    params, i18n keys and live slice values ONLY.
 */
export default function MyBoardPage({ scope }: { scope: 'multi' | 'single' }) {
  return scope === 'multi' ? <MultiBoard /> : <SingleBoard />
}

/**
 * The caller's REAL engagements — `GET /{orgId}/my-work` rows with the
 * caller's own grant. Reused (not re-fetched) by both `MultiBoard` and
 * `SingleBoard` project resolution below.
 */
function MyEngagements() {
  const { t } = useTranslation('myWork')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const rows = useAppSelector(selectMyWorkRows)
  const loading = useAppSelector(selectMyWorkLoading)
  const error = useAppSelector(selectMyWorkError)

  useEffect(() => {
    if (orgId !== null) void dispatch(fetchMyWork(orgId))
  }, [dispatch, orgId])

  const onRetryClicked = useCallback((): void => {
    if (orgId !== null) void dispatch(fetchMyWork(orgId))
  }, [dispatch, orgId])

  return (
    <section aria-label={t('engagements.title')} className="mt-5" data-testid="my-engagements">
      <SectionLabel>{t('engagements.title')}</SectionLabel>
      {error !== null ? (
        <div className="mt-1.5">
          {error.status === 403 ? (
            <ForbiddenPanel requested="/{orgId}/my-work" />
          ) : (
            <ErrorPanel error={asApiError(error)} onRetryClicked={onRetryClicked} />
          )}
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="mt-1.5">
          <LoadingPanel label={t('engagements.loading')} />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-1.5" data-testid="my-engagements-empty">
          <EmptyPanel title={t('engagements.title')} body={t('engagements.empty')} />
        </div>
      ) : (
        <ul className="mt-1.5 overflow-hidden rounded-lg border border-border bg-card">
          {rows.map((row) => (
            <li key={row.id} className="v-row flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
              <span className="text-sm font-medium text-foreground">{row.name}</span>
              <StatusPill tone="neutral">{phaseDisplayName(row.currentPhase)}</StatusPill>
              <StatusPill tone={healthTone(row.health)}>{row.health ?? '—'}</StatusPill>
              <span className="text-xs text-muted-foreground">
                {t('engagements.columns.goLive')}: {row.goLiveDate ? isoDayLabel(row.goLiveDate) : t('engagements.noGoLive')}
              </span>
              <span className="w-full text-xs text-muted-foreground sm:ml-auto sm:w-auto">
                {t('engagements.grant', {
                  role: row.callerMember.role ?? '—',
                  access: row.callerMember.accessLevel ?? '—',
                  side:
                    row.callerMember.side === 'CLIENT'
                      ? t('engagements.sideCLIENT')
                      : t('engagements.sideFIRM')
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * The selected task's review column, identical on both boards: the artefact
 * under review with its verdicts, and the run log beside it. All three panels
 * read the selection AND the project off the board slice, so this takes no
 * props — the caller only decides WHETHER to render it.
 */
function TaskReviewPanels() {
  return (
    <section className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div>
        <ReviewCard />
        <BoardVerdictActions />
      </div>
      <div>
        <RunLogPanel />
      </div>
    </section>
  )
}

function MultiBoard() {
  const { t } = useTranslation(['myWork', 'common'])
  const role = useAppSelector(selectActiveRoleKey)
  const myWorkRows = useAppSelector(selectMyWorkRows)
  const selectedTask = useAppSelector(selectSelectedTask)
  const [params, setParams] = useSearchParams()
  const requested = params.get('project')
  const known = requested !== null && myWorkRows.some((row) => row.id === requested)
  // AC-5 / AC-9: one engagement's tasks at a time — the ?project= filter,
  // defaulting to the first my-work row. No my-work rows -> no project, no
  // tasks request (BuildQueue itself issues none while projectId is null).
  const boardProjectId = known ? requested : (myWorkRows[0]?.id ?? null)

  const onSelectProject = useCallback(
    (projectId: string) => setParams({ project: projectId }),
    [setParams]
  )

  return (
    <AppShell
      moduleLabel={t('myWork:board.multiModuleLabel')}
      breadcrumb={t('myWork:board.multiBreadcrumb')}
      contextPill={t('myWork:chrome.contextPill', {
        role: t(roleDefinitionFor(role).labelKey, { ns: 'common' }),
        count: myWorkRows.length
      }).toUpperCase()}
      nav={myNav(role)}
    >
      <div className="w-full">
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">{t('myWork:board.multiTitle')}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t('myWork:board.multiLede')}</p>
        </header>

        <MyEngagements />

        {myWorkRows.length > 0 && (
          // Deferred finding c1-3 (ruling, Agon 2026-09-23): the "All
          // engagements" chip is REMOVED — clearing the filter never showed
          // all engagements' tasks at once (no cross-project projection
          // exists server-side, CR on TASK-FCC2C3E4), it only fell back to
          // `myWorkRows[0]`, so the control read as pressed while showing one
          // project's board. Every chip now reads `aria-pressed` off
          // `boardProjectId` (the engagement ACTUALLY shown), not `requested`
          // — with no `?project=`, that is the first row, so its chip is the
          // one that reads pressed instead of none of them.
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <SectionLabel className="mr-1">{t('myWork:board.filterLabel')}</SectionLabel>
            {myWorkRows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => onSelectProject(row.id)}
                aria-pressed={boardProjectId === row.id}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs',
                  boardProjectId === row.id
                    ? 'bg-primary font-medium text-primary-foreground'
                    : 'border border-input bg-card text-ink-soft hover:bg-muted'
                )}
              >
                {row.name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-5">
          {boardProjectId === null ? (
            <EmptyPanel title={t('myWork:board.noEngagementsTitle')} body={t('myWork:board.noEngagementsBody')} />
          ) : (
            <BuildQueue key={boardProjectId} projectId={boardProjectId} narrowToCaller mode="tasksOnly" />
          )}
        </div>

        {selectedTask !== null && boardProjectId !== null && <TaskReviewPanels />}
      </div>
    </AppShell>
  )
}

function SingleBoard() {
  const { t } = useTranslation('myWork')
  const role = useAppSelector(selectActiveRoleKey)
  const myWorkRows = useAppSelector(selectMyWorkRows)
  const myWorkLoading = useAppSelector(selectMyWorkLoading)
  const myWorkError = useAppSelector(selectMyWorkError)
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const selectedTask = useAppSelector(selectSelectedTask)

  useEffect(() => {
    if (orgId !== null) void dispatch(fetchMyWork(orgId))
  }, [dispatch, orgId])

  const onRetryClicked = useCallback((): void => {
    if (orgId !== null) void dispatch(fetchMyWork(orgId))
  }, [dispatch, orgId])

  // AC-10: exactly the ONE project this caller's my-work row names — no
  // filter control, no fixture project.
  const engagement = myWorkRows[0] ?? null

  return (
    <AppShell
      moduleLabel={t('board.singleModuleLabel')}
      breadcrumb={engagement?.name ?? t('board.singleNoEngagement')}
      contextPill={engagement !== null ? phaseDisplayName(engagement.currentPhase).toUpperCase() : t('board.singleNoEngagement')}
      nav={myNav(role)}
    >
      <div className="w-full">
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">
            {engagement?.name ?? t('board.singleNoEngagement')}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{t('board.singleLede')}</p>
        </header>

        <div className="mt-5">
          {myWorkError !== null ? (
            <ErrorPanel
              error={asApiError({ status: myWorkError.status, key: myWorkError.key, message: myWorkError.message })}
              onRetryClicked={onRetryClicked}
            />
          ) : myWorkLoading && engagement === null ? (
            <LoadingPanel label={t('engagements.loading')} />
          ) : engagement === null ? (
            <EmptyPanel title={t('board.noEngagementsTitle')} body={t('board.noEngagementsBody')} />
          ) : (
            // Deferred finding c1-2 (ruling, Agon 2026-09-23): `/my/project`
            // NARROWS to the caller exactly like `/my/board` — the owning
            // ticket's own AC-5 ("both routes narrow to the caller") wins
            // over the approved spec's AC-10, which omitted the prop.
            <BuildQueue key={engagement.id} projectId={engagement.id} narrowToCaller mode="tasksOnly" />
          )}
        </div>

        {selectedTask !== null && engagement !== null && <TaskReviewPanels />}
      </div>
    </AppShell>
  )
}

